/**
 * realtimeClient.ts — the browser half of the self-owned realtime pipeline.
 *
 * Replaces a supabase-js `postgres_changes` subscription with a thin
 * EventSource client against the FastAPI SSE endpoint
 * (`GET {base}/events?room=…&access_token=…`). Each `data:` frame is an
 * envelope shaped exactly like a Supabase postgres_changes payload
 * (`{ t, op, new | old }`), so callers keep their existing handlers.
 *
 * Transport facts this client has to work around:
 *   - `EventSource` cannot set an `Authorization` header, so the session JWT
 *     travels as `?access_token=` (the backend's SSE endpoint is built for
 *     this — see `app/api/v1/events.py`).
 *   - `EventSource` auto-reconnects, but the Supabase session token expires
 *     (~1 h) and is rotated by supabase-js. After `MAX_CONSECUTIVE_ERRORS`
 *     the client tears the stream down and rebuilds it with a fresh token —
 *     that covers the expiry window without the caller doing anything.
 *   - Redis pub/sub has no replay, so a reconnect can miss events. The
 *     catch-up contract: `onOpen` fires on every (re)connect and the caller
 *     re-fetches recent rows from the REST API.
 *
 * If the backend is not configured (workspace not opted in) or there is no
 * session, the subscription is inert and `onError` fires once — the caller is
 * expected to fall back to its previous transport.
 */

import { supabase } from './supabase';
import { apiConfigured, getApiBaseUrl } from './apiClient';

/** The envelope shape — mirrors Supabase postgres_changes payloads. */
export interface RealtimeEnvelope {
  t: string;
  op: 'INSERT' | 'UPDATE' | 'DELETE';
  new?: Record<string, unknown>;
  old?: { id?: string } | null;
}

export interface RealtimeSubscription {
  /** Tear down the stream and stop reconnecting. Idempotent. */
  close(): void;
}

export interface SubscribeRealtimeOptions {
  /** Fired for every `data:` frame whose envelope parses. */
  onEvent: (event: RealtimeEnvelope) => void;
  /** Fired on every (re)connect — use to re-fetch and fill any gap. */
  onOpen?: () => void;
  /** Fired on stream errors and when the subscription is inert. */
  onError?: (reason: string) => void;
}

/** Consecutive `error` events before we rebuild the stream with a new token. */
const MAX_CONSECUTIVE_ERRORS = 3;

export function subscribeRoom(
  room: string,
  options: SubscribeRealtimeOptions,
): RealtimeSubscription {
  let es: EventSource | null = null;
  let closed = false;
  let errors = 0;
  let errorNotified = false;

  const notifyError = (reason: string) => {
    if (errorNotified) return;
    errorNotified = true;
    options.onError?.(reason);
  };

  const build = () => {
    if (closed) return;
    if (es) {
      es.close();
      es = null;
    }
    if (!apiConfigured()) {
      notifyError('Backend not configured');
      return;
    }
    // Token is async — the session may not be resolved yet.
    // eslint-disable-next-line no-restricted-syntax -- auth session read, not a data query; no swallowable fetch error
    void supabase.auth.getSession().then(({ data }) => {
      if (closed) return;
      const token = data.session?.access_token;
      if (!token) {
        notifyError('No session');
        return;
      }
      const url =
        `${getApiBaseUrl()}/events` +
        `?room=${encodeURIComponent(room)}` +
        `&access_token=${encodeURIComponent(token)}`;
      const next = new EventSource(url);

      next.onopen = () => {
        errors = 0;
        options.onOpen?.();
      };

      next.onmessage = (msg) => {
        try {
          options.onEvent(JSON.parse(msg.data) as RealtimeEnvelope);
        } catch {
          // Malformed frame — drop it; the next one may be fine.
        }
      };

      next.onerror = () => {
        errors += 1;
        options.onError?.('realtime stream error');
        if (errors >= MAX_CONSECUTIVE_ERRORS) {
          // Most likely a stale token — rebuild with a fresh one.
          errors = 0;
          build();
        }
      };

      es = next;
    });
  };

  build();

  return {
    close() {
      closed = true;
      if (es) {
        es.close();
        es = null;
      }
    },
  };
}

// ── WebSocket half: ticket presence + typing ─────────────────────────────────
//
// The one bidirectional surface (ticket presence/typing) runs over a WebSocket
// to `{base}/ws?access_token=…` (see backend app/api/v1/ws.py). Protocol:
//
//   client → server:  {"type":"join","room":"ticket:<id>","presence":{…}}
//                     {"type":"typing"}
//                     {"type":"leave"}
//   server → client:  {"type":"presence","room":…,"others":[{…}]}
//                     {"type":"typing","room":…,"user_id":…}
//
// `others` excludes the broadcaster; receivers filter their own id too (the
// hook mirrors useTicketPresence's `if (userId === me.user_id) continue`).
//
// Unlike EventSource, the browser WebSocket does NOT auto-reconnect, so this
// client owns the reconnect loop: on close it re-opens, and after
// MAX_CONSECUTIVE_ERRORS it rebuilds with a fresh session token (covers the
// ~1 h token rotation). `join` is re-sent on every (re)open so presence state
// is refreshed, matching the WS server's broadcast-on-join behaviour.

export interface PresenceRowPayload {
  user_id: string;
  full_name: string;
  avatar_url?: string | null;
  role?: string;
}

export interface PresenceSubscription {
  /** Broadcast a typing frame to every joined room. */
  signalTyping(): void;
  /** Send leave + tear the socket down. Idempotent. */
  close(): void;
}

export interface SubscribePresenceOptions {
  onPresence: (others: PresenceRowPayload[]) => void;
  onTyping: (userId: string) => void;
  onError?: (reason: string) => void;
}

/** Reconnect delay between WS close and re-open (no backoff for now). */
const WS_RECONNECT_MS = 1500;

/**
 * Convert the HTTP API base to a WebSocket base (`http` → `ws`, `https` → `wss`).
 *
 * A RELATIVE base (`/api/v1`) has to be resolved against the current page.
 * fetch() and EventSource both accept a relative URL, but WebSocket does not —
 * it needs a scheme and a host. The previous fallback prefixed the string with
 * `ws://` regardless, turning `/api/v1` into `ws:///api/v1`: three slashes, no
 * host, and every presence connection failed.
 *
 * This matters now because the container image is built with the relative
 * `/api/v1` on purpose, so one bundle works behind any hostname — on the VPS
 * or on AWS — without a rebuild.
 */
function wsBaseFromApiBase(apiBase: string): string {
  if (apiBase.startsWith('https://')) return `wss://${apiBase.slice(8)}`;
  if (apiBase.startsWith('http://')) return `ws://${apiBase.slice(7)}`;

  // Relative (or empty): resolve against the page's own origin, matching the
  // page's scheme so an https page never opens an insecure socket.
  if (typeof window !== 'undefined' && window.location) {
    const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const path = apiBase.startsWith('/') ? apiBase : `/${apiBase}`;
    return `${scheme}//${window.location.host}${path}`;
  }
  return `ws://${apiBase.replace(/^\//, '')}`;
}

export function subscribePresenceRoom(
  room: string,
  me: PresenceRowPayload,
  options: SubscribePresenceOptions,
): PresenceSubscription {
  let ws: WebSocket | null = null;
  let closed = false;
  let errors = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let errorNotified = false;

  const notifyError = (reason: string) => {
    if (errorNotified) return;
    errorNotified = true;
    options.onError?.(reason);
  };

  const scheduleReconnect = () => {
    if (closed) return;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      build();
    }, WS_RECONNECT_MS);
  };

  const build = () => {
    if (closed) return;
    if (ws) {
      // Avoid firing onerror from the teardown socket.
      ws.onclose = null;
      ws.close();
      ws = null;
    }
    if (!apiConfigured()) {
      notifyError('Backend not configured');
      return;
    }
    // eslint-disable-next-line no-restricted-syntax -- auth session read, not a data query; no swallowable fetch error
    void supabase.auth.getSession().then(({ data }) => {
      if (closed) return;
      const token = data.session?.access_token;
      if (!token) {
        notifyError('No session');
        return;
      }
      const url =
        `${wsBaseFromApiBase(getApiBaseUrl())}/ws` +
        `?access_token=${encodeURIComponent(token)}`;
      const next = new WebSocket(url);

      next.onopen = () => {
        errors = 0;
        next.send(JSON.stringify({
          type: 'join',
          room,
          presence: me,
        }));
      };

      next.onmessage = (msg) => {
        let parsed: { type?: string; others?: PresenceRowPayload[]; user_id?: string };
        try {
          parsed = JSON.parse(String(msg.data));
        } catch {
          return; // Malformed frame — drop it.
        }
        if (parsed.type === 'presence' && Array.isArray(parsed.others)) {
          options.onPresence(parsed.others);
        } else if (parsed.type === 'typing' && typeof parsed.user_id === 'string') {
          options.onTyping(parsed.user_id);
        }
      };

      next.onerror = () => {
        errors += 1;
        options.onError?.('realtime ws error');
      };

      next.onclose = () => {
        if (closed) return;
        if (errors >= MAX_CONSECUTIVE_ERRORS) {
          // Most likely a stale token — rebuild with a fresh one.
          errors = 0;
          build();
          return;
        }
        scheduleReconnect();
      };

      ws = next;
    });
  };

  build();

  return {
    signalTyping() {
      if (closed || !ws || ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(JSON.stringify({ type: 'typing' }));
      } catch {
        // Network blip — losing one typing frame doesn't matter.
      }
    },
    close() {
      closed = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (ws) {
        try {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'leave' }));
          }
        } catch {
          // Socket already gone.
        }
        ws.onclose = null;
        ws.close();
        ws = null;
      }
    },
  };
}
